// The order of the change-over from a won Sight Reading tier to what the
// player picks next.
//
// A tier is won when the count toward the next tier reaches 0. From there the
// screen goes, in this order and each step once:
//
//   1. won: playback stops and the tier-complete overlay dims the finished tier
//      while its completion is recorded and the wrap-up is fetched;
//   2. wrap-up: the completion modal opens on the finished tier's summary;
//   3. choice: Replay Tier or Next asks for the play session that follows
//      (beginWonTierChoice, then the caller's request). The screen answers at
//      once: the modal and the score under it fade out together, with the
//      header's content ("fadingOut"). A second press is ignored;
//   4. waiting: the old tier is gone and the new one is not in yet. The screen
//      stays calm (an indicator only if the wait runs long), the header's Back
//      stays in reach, and nothing can be played;
//   5. arrival: when the next session has arrived (and not before the fade-out
//      has finished), the finished tier's wrap-up state is cleared in one step.
//      The count stays at 0 until the lesson has torn the finished tier down for
//      the new session, which sets it back to its start;
//   6. fadingIn: once that reset is done and the new score has its first phrase,
//      the score and the header's content fade in together, the header now
//      showing the new tier or level. Then the screen is steady again.
//
// Next at the level's last tier that leaves the lesson (for a tutorial) takes
// step 3 only, once.
//
// With reduced motion every fade is instant: the old content goes at the press
// and the new content appears when it is ready.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as lessonActions from 'Actions/lesson';
import { AppDispatch } from 'Reducers';
import { LessonReducer } from 'Reducers/lessonReducer';
import { MIN_NUM_PHRASES } from 'Utils/Constants';

/**
 * How long the overlay may hold after the next session has arrived. The
 * lesson's own reset ends the hold; if that reset fails (its tier list request
 * throws), the overlay is lifted after this so the screen does not stay dimmed.
 */
export const TIER_OVERLAY_SAFETY_RELEASE_MS = 10000;

/** How long the modal, the score and the header's content take to fade out. */
export const TIER_CHANGE_FADE_OUT_MS = 300;

/** How long the new score and the header's content take to fade in. */
export const TIER_CHANGE_FADE_IN_MS = 400;

/**
 * How long, after the lesson's reset for the new session, the fade-in waits
 * for the new score's first phrase before it goes ahead without it.
 */
export const TIER_CHANGE_SCORE_WAIT_MS = 2500;

/** How long the screen may sit faded out before it shows that it is loading. */
export const TIER_CHANGE_WAIT_INDICATOR_DELAY_MS = 700;

/**
 * Where the change-over stands:
 * - steady: no change-over (playing, won, or the wrap-up open);
 * - fadingOut: a choice was made; the old content is fading out;
 * - waiting: the old content is gone; the new session or score is not ready;
 * - fadingIn: the new score and header content are fading in.
 */
export type TierChangeOverPhase = 'steady' | 'fadingOut' | 'waiting' | 'fadingIn';

interface TierCompleteTransitionInput {
  /**
   * Clears the lesson screen's own state for the finished tier's wrap-up (its
   * carousel, the repertoire it unlocked, the accuracy readout). Called once,
   * when the next session has arrived and the old content has faded out.
   */
  onNextSessionArrived: () => void;
  /** The player asked for reduced motion: no fades, instant switches. */
  reducedMotion: boolean;
  /** The play session the score last received a phrase for. */
  scoreServedFor: string | null;
}

interface TierCompleteTransition {
  /**
   * Claims the player's choice after a won tier (Replay, Next): true the first
   * time, and the caller then asks for the next session or leaves; false while
   * an earlier choice is still under way, and the caller does nothing.
   */
  beginWonTierChoice: () => boolean;
  phase: TierChangeOverPhase;
  /** How long the fade under way takes, in ms (0 with reduced motion). */
  fadeMs: number;
  /** The wait has run long enough to show that the next tier is loading. */
  showWaitIndicator: boolean;
}

/** The old content is going or gone, and nothing new is on screen yet. */
export const tierChangeHidesContent = (phase: TierChangeOverPhase): boolean =>
  phase === 'fadingOut' || phase === 'waiting';

export const useTierCompleteTransition = ({
  onNextSessionArrived,
  reducedMotion,
  scoreServedFor,
}: TierCompleteTransitionInput): TierCompleteTransition => {
  const dispatch = useDispatch<AppDispatch>();
  const playSessionId = useSelector(
    (state: LessonReducer) => state.lessonReducer.currentUserLevelData?.play_session?.id ?? null,
  );
  const phrasesTilNextTier = useSelector(
    (state: LessonReducer) => state.lessonReducer.phrasesTilNextTier,
  );
  const hasTierSummary = useSelector(
    (state: LessonReducer) => state.lessonReducer.tierSummary !== null,
  );

  const [phase, setPhase] = useState<TierChangeOverPhase>('steady');
  // The session a choice was made on, while its follower is on its way. A ref,
  // so two presses in one frame cannot both get through.
  const choiceMadeOnRef = useRef<string | null>(null);
  // The session that arrived after a choice, until its wrap-up clear has run.
  const [arrivedSession, setArrivedSession] = useState<string | null>(null);
  // The session that arrived after a choice, while the overlay still holds for it.
  const [holdingOverlayFor, setHoldingOverlayFor] = useState<string | null>(null);
  // The arrived session the lesson has reset for, while its score is awaited.
  const [resetDoneFor, setResetDoneFor] = useState<string | null>(null);
  const [showWaitIndicator, setShowWaitIndicator] = useState(false);
  const onNextSessionArrivedRef = useRef(onNextSessionArrived);
  onNextSessionArrivedRef.current = onNextSessionArrived;
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;

  // 1. Won: playback stops. Stopped, not toggled: a tier won while the lesson
  // was already paused stays paused.
  useEffect(() => {
    if (phrasesTilNextTier === 0) {
      dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));
    }
  }, [phrasesTilNextTier, dispatch]);

  // Any change of session: a session that follows a choice is held for its
  // clear (5); any other change drops the summary of the session that was left.
  const previousSessionRef = useRef(playSessionId);
  useEffect(() => {
    const previousSession = previousSessionRef.current;
    previousSessionRef.current = playSessionId;
    if (playSessionId === previousSession) return;
    const choiceMadeOn = choiceMadeOnRef.current;
    if (choiceMadeOn !== null && playSessionId && playSessionId !== choiceMadeOn) {
      choiceMadeOnRef.current = null;
      setArrivedSession(playSessionId);
      return;
    }
    if (hasTierSummary) dispatch(lessonActions.setTierSummary(null));
  }, [playSessionId, hasTierSummary, dispatch]);

  // 3 -> 4: the fade-out runs its course.
  useEffect(() => {
    if (phase !== 'fadingOut') return undefined;
    const fadedOut = setTimeout(() => setPhase('waiting'), TIER_CHANGE_FADE_OUT_MS);
    return () => clearTimeout(fadedOut);
  }, [phase]);

  // 5. Arrival: once the old content is out of sight, the wrap-up shown
  // belongs to the session that was left.
  useEffect(() => {
    if (arrivedSession === null || phase !== 'waiting') return;
    setArrivedSession(null);
    dispatch(lessonActions.setShowWrapUp(false));
    dispatch(lessonActions.setWrapUpIndex(0));
    dispatch(lessonActions.setTierSummary(null));
    dispatch(lessonActions.setTierSummaryRequestError(false));
    dispatch(lessonActions.resetCompletedPhrasesCount());
    onNextSessionArrivedRef.current();
    setHoldingOverlayFor(arrivedSession);
  }, [arrivedSession, phase, dispatch]);

  // The overlay's hold ends when the count is back at its start (the lesson's
  // reset for the new session); the safety release lifts it if that never
  // happens.
  useEffect(() => {
    if (holdingOverlayFor === null) return undefined;
    if (playSessionId !== holdingOverlayFor) {
      setHoldingOverlayFor(null);
      return undefined;
    }
    if (phrasesTilNextTier !== 0) {
      setHoldingOverlayFor(null);
      setResetDoneFor(holdingOverlayFor);
      return undefined;
    }
    const release = setTimeout(() => {
      dispatch(lessonActions.updatePhrasesTilNextTier(MIN_NUM_PHRASES));
    }, TIER_OVERLAY_SAFETY_RELEASE_MS);
    return () => clearTimeout(release);
  }, [holdingOverlayFor, phrasesTilNextTier, playSessionId, dispatch]);

  // 6. The new score is ready when the lesson has reset for the new session and
  // the score has its first phrase for it (or has had long enough to get one).
  useEffect(() => {
    if (resetDoneFor === null || phase !== 'waiting') return undefined;
    const fadeIn = () => {
      setResetDoneFor(null);
      setPhase(reducedMotionRef.current ? 'steady' : 'fadingIn');
    };
    if (playSessionId !== resetDoneFor || scoreServedFor === resetDoneFor) {
      fadeIn();
      return undefined;
    }
    const goAheadWithoutIt = setTimeout(fadeIn, TIER_CHANGE_SCORE_WAIT_MS);
    return () => clearTimeout(goAheadWithoutIt);
  }, [resetDoneFor, scoreServedFor, playSessionId, phase]);

  useEffect(() => {
    if (phase !== 'fadingIn') return undefined;
    const fadedIn = setTimeout(() => setPhase('steady'), TIER_CHANGE_FADE_IN_MS);
    return () => clearTimeout(fadedIn);
  }, [phase]);

  // A long wait shows that the next tier is loading.
  useEffect(() => {
    if (phase !== 'waiting') {
      setShowWaitIndicator(false);
      return undefined;
    }
    const indicate = setTimeout(
      () => setShowWaitIndicator(true),
      TIER_CHANGE_WAIT_INDICATOR_DELAY_MS,
    );
    return () => clearTimeout(indicate);
  }, [phase]);

  // 3. Choice.
  const beginWonTierChoice = useCallback(() => {
    if (choiceMadeOnRef.current !== null) return false;
    choiceMadeOnRef.current = playSessionId ?? '';
    // Nothing plays while the finished tier waits for what follows it: a
    // session arriving while play was on would be taken for a won tier.
    dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));
    setPhase(reducedMotionRef.current ? 'waiting' : 'fadingOut');
    return true;
  }, [playSessionId, dispatch]);

  const fadeMs = reducedMotion
    ? 0
    : phase === 'fadingIn'
      ? TIER_CHANGE_FADE_IN_MS
      : TIER_CHANGE_FADE_OUT_MS;

  return {
    beginWonTierChoice,
    phase,
    fadeMs,
    showWaitIndicator,
  };
};
